'use client';

/**
 * Chip — bordered mono-caps pill used across yarn cards, project cards,
 * detail rows, etc. The B1 zine "metadata tag" pattern: 1px ink border,
 * paper-1 fill, tiny tracked-out mono uppercase text.
 *
 * Styling pulls from the global design tokens defined in
 * `src/app/globals.css` (`--paper-1`, `--ink`, `--ink-2`, `--font-mono`).
 * To restyle every chip in the app, change those tokens — not this
 * component.
 *
 * Variants (build 1357):
 *   bold        — fontWeight 700 instead of 600. Use for chip-buttons
 *                 that name another row (project chips on yarn cards,
 *                 technique chips on project pages — the ones that
 *                 read as nav links).
 *   size="xs"   — fontSize 8 + tighter padding + wider tracking. Use
 *                 inside dense rows (active-page yarn + needle chips).
 *
 * Polymorphic:
 *   Pass `onClick` and the Chip renders as a `span[role="button"]` (with
 *   keyboard support). This allows actionable chips to live safely inside
 *   larger clickable regions (e.g. project cards on /active) without
 *   nested interactive element violations. Without `onClick` it is a plain
 *   `<span>` — pure metadata tag.
 *
 * Tone variants:
 *   `tone="accent"`  — orange pill. Replaces scattered `text-accent bg-accent/10`.
 *   `tone="success"` — green for available/positive status.
 *   `tone="warning"` — amber for caution states.
 *   `tone="danger"`  — red for over-allocated / conflict / error states.
 *
 * The status tones are designed to work on the cream paper background while
 * remaining readable and consistent with the B1 zine aesthetic.
 */

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

export type ChipSize = 'sm' | 'xs';
export type ChipTone = 'default' | 'accent' | 'success' | 'warning' | 'danger';

export type ChipProps = {
  children: ReactNode;
  /** Per-instance overrides — use sparingly. */
  style?: CSSProperties;
  className?: string;
  /** Heavier text weight. Used for chip-buttons that name another row. */
  bold?: boolean;
  /** `sm` (default) or `xs` for denser surfaces. */
  size?: ChipSize;
  /** Visual tone. `accent` produces the orange pill used for status, type, etc. */
  tone?: ChipTone;
  /** Provide to render as a real `<button>` instead of a `<span>`.
   *  Receives the native event so callers can `preventDefault` /
   *  `stopPropagation` to avoid bubbling into a parent link. */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Native title tooltip — useful when chip text is truncated by ellipsis. */
  title?: string;
  'aria-label'?: string;
};

const SIZE_STYLES: Record<ChipSize, CSSProperties> = {
  sm: { fontSize: 9, letterSpacing: '0.1em', padding: '2px 7px' },
  xs: { fontSize: 8, letterSpacing: '0.1em', padding: '1px 5px' },
};

export function Chip({
  children,
  style,
  className,
  bold = false,
  size = 'sm',
  tone = 'default',
  onClick,
  title,
  'aria-label': ariaLabel,
}: ChipProps) {
  let toneStyles: CSSProperties = {};

  switch (tone) {
    case 'accent':
      toneStyles = {
        color: 'var(--color-accent)',
        border: '1px solid var(--color-accent)',
        background: 'rgba(251, 94, 0, 0.10)',
      };
      break;
    case 'success':
      toneStyles = {
        color: '#166534',           // dark green
        border: '1px solid #86efac',
        background: '#dcfce7',      // light green
      };
      break;
    case 'warning':
      toneStyles = {
        color: '#854d0e',           // dark amber
        border: '1px solid #fde047',
        background: '#fef9c3',      // light yellow
      };
      break;
    case 'danger':
      toneStyles = {
        color: 'var(--over-num)',
        border: '1px solid var(--over)',
        background: 'rgba(228, 0, 0, 0.08)',
      };
      break;
    default:
      toneStyles = {};
  }

  const baseStyle: CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontWeight: bold ? 700 : 600,
    color: 'var(--ink-2)',
    textTransform: 'uppercase',
    border: '1px solid var(--ink)',
    background: 'var(--paper-1)',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    ...SIZE_STYLES[size],
    ...toneStyles,
    ...style,
  };

  if (onClick) {
    // Render as span[role="button"] instead of a real <button> so that
    // Chips with onClick can safely live inside larger clickable regions
    // (e.g. project cards in /active) without triggering the nested
    // interactive element rule or hydration warnings.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e as any);
      }
    };

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={className}
        title={title}
        aria-label={ariaLabel}
        style={{
          ...baseStyle,
          cursor: 'pointer',
          outline: 'none', // we handle focus via the parent card or explicit ring if needed
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <span className={className} title={title} aria-label={ariaLabel} style={baseStyle}>
      {children}
    </span>
  );
}
